import { CanvasTexture, LinearFilter, ClampToEdgeWrapping, RepeatWrapping } from 'three';

/**
 * One texture holding every inscription a seal can wear.
 *
 * The brief forbids a mesh per character, and rightly: twenty-six waves with
 * five inscribed rings each would be hundreds of draw calls for decoration.
 * Instead every ring is a **row of this atlas**, and the ripple shader maps a
 * ring's angle onto that row's U axis. The whole figure therefore costs one
 * texture fetch inside a draw that was already happening, and the cost does not
 * grow with wave count.
 *
 * ## Two kinds of row
 *
 * **Continuous strips** carry running text that tiles seamlessly in U. They are
 * laid out by measuring: clusters are placed left to right until the row is
 * full, then the leftover is spread evenly between them, so the strip ends
 * exactly where it began and the seam falls in a gap. These are what fill a
 * ring's whole circumference — the shader picks a repeat count from the wave's
 * circumference, so a bigger circle gets *more text*, never wider text.
 *
 * **Tiled rows** hold the six discrete note words, one per cell, addressed by
 * slot. That ring is the one a later phase will judge against, so it stays
 * legible and evenly spaced rather than packed.
 *
 * Devanagari is used as a decorative script here, not as language: these are
 * mantra fragments and seed syllables chosen to look like a ritual inscription,
 * and no claim is made about the rings forming grammatical text. Nothing about
 * their meaning reaches the simulation.
 */

/** Discrete words on the note ring, and therefore cells in a note row. */
export const NOTE_WORDS_PER_RING = 6;

/** Per-wave variants. `pattern` selects within each family by this modulus. */
export const PATTERN_COUNT = 6;

/** Ordered so adjacent variants do not share an obvious silhouette. */
const NOTE_WORDS: readonly (readonly string[])[] = [
  ['ॐ', 'नमः', 'शिवाय', 'ह्रीं', 'क्लीं', 'स्वाहा'],
  ['ओं', 'मणि', 'पद्मे', 'हूं', 'श्रीं', 'फट्'],
  ['श्री', 'महा', 'लक्ष्म्यै', 'नमः', 'ह्रीं', 'ऐं'],
  ['ॐ', 'गं', 'गणपतये', 'नमः', 'स्वाहा', 'श्रीं'],
  ['ऐं', 'सरस्वत्यै', 'नमः', 'ह्रीं', 'ॐ', 'वद'],
  ['भूः', 'भुवः', 'स्वः', 'तत्', 'सवितुः', 'वरेण्यं'],
];

/** Outermost ring: long running mantra, drawn largest. */
const OUTER_MANTRAS: readonly (readonly string[])[] = [
  ['ॐ', 'नमः', 'शिवाय', 'ॐ', 'ह्रीं', 'क्लीं', 'सौः', 'ऐं', 'नमः', 'शिवाय'],
  ['ॐ', 'मणि', 'पद्मे', 'हूं', 'ॐ', 'श्रीं', 'ह्रीं', 'क्लीं', 'मणि', 'पद्मे'],
  ['ॐ', 'गं', 'गणपतये', 'नमः', 'स्वाहा', 'ऐं', 'ह्रीं', 'श्रीं', 'गणपतये', 'नमः'],
];

/** Second ring: shorter formulae, denser because the band is thinner. */
const SECONDARY_MANTRAS: readonly (readonly string[])[] = [
  ['ह्रीं', 'क्लीं', 'ऐं', 'सौः', 'श्रीं', 'हूं', 'फट्', 'ॐ', 'ह्रीं', 'क्लीं', 'नमः'],
  ['नमः', 'स्वाहा', 'वषट्', 'हुं', 'फट्', 'ॐ', 'ऐं', 'ह्रीं', 'नमः', 'स्वाहा', 'श्रीं'],
  ['भूः', 'भुवः', 'स्वः', 'तत्', 'सवितुः', 'वरेण्यं', 'भर्गो', 'देवस्य', 'धीमहि', 'ॐ'],
];

/** Inner rings: seed syllables, the shortest and densest of the three. */
const BIJA_SYLLABLES: readonly (readonly string[])[] = [
  ['ॐ', 'ह्रीं', 'क्लीं', 'ऐं', 'सौः', 'हूं', 'फट्', 'श्रीं', 'ह्रौं', 'ग्लौं'],
  ['अं', 'आं', 'इं', 'ईं', 'उं', 'ऊं', 'एं', 'ओं', 'अः', 'ॐ'],
  ['क्षं', 'मं', 'यं', 'रं', 'लं', 'वं', 'शं', 'हं', 'सं', 'ॐ'],
];

/** Font stack. Windows ships Nirmala UI; the rest cover other platforms. */
const FONT_STACK = '"Nirmala UI", "Noto Sans Devanagari", "Mangal", "Aparajita", sans-serif';

/**
 * Row layout. The shader addresses rows through these bases, so the two files
 * agree by construction rather than by a comment saying they should.
 */
export const ATLAS_ROWS = {
  note: { base: 0, variants: NOTE_WORDS.length },
  outer: { base: 6, variants: OUTER_MANTRAS.length },
  secondary: { base: 9, variants: SECONDARY_MANTRAS.length },
  bija: { base: 12, variants: BIJA_SYLLABLES.length },
  ornament: { base: 15, variants: 1 },
} as const;

const TOTAL_ROWS = ATLAS_ROWS.ornament.base + ATLAS_ROWS.ornament.variants;

export interface GlyphAtlasOptions {
  /** Height of one row in pixels. */
  readonly rowHeight?: number;
  /** Width of one note cell. Row width is this times the note word count. */
  readonly noteCellWidth?: number;
}

export interface GlyphAtlasReport {
  readonly rows: number;
  readonly rowHeight: number;
  readonly width: number;
  readonly height: number;
  readonly noteWordsPerRing: number;
  readonly noteCellAspect: number;
  /** Row width ÷ row height. Sets how much text one repeat of a strip holds. */
  readonly stripAspect: number;
  /** Clusters drawn into each continuous strip, by family. */
  readonly clustersPerStrip: Record<string, number>;
  /** Fraction of pixels with ink. Near zero means the script did not render. */
  readonly inkFraction: number;
  /** Per-row ink, so a row that failed to render cannot hide in the average. */
  readonly rowInkFraction: readonly number[];
  readonly devanagariRendered: boolean;
  readonly bytes: number;
  readonly megabytes: number;
}

export class GlyphAtlas {
  readonly texture: CanvasTexture;
  readonly rows = TOTAL_ROWS;
  readonly noteWords = NOTE_WORDS_PER_RING;
  readonly patternCount = PATTERN_COUNT;
  /** Note cell width ÷ row height. */
  readonly noteAspect: number;
  /** Full row width ÷ row height, for the continuous strips. */
  readonly stripAspect: number;
  readonly report: GlyphAtlasReport;

  constructor(options: GlyphAtlasOptions = {}) {
    const rowHeight = options.rowHeight ?? 96;
    const noteCellWidth = options.noteCellWidth ?? 340;
    const width = noteCellWidth * NOTE_WORDS_PER_RING;
    this.noteAspect = noteCellWidth / rowHeight;
    this.stripAspect = width / rowHeight;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = rowHeight * TOTAL_ROWS;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (context === null) throw new Error('2D canvas context unavailable for the glyph atlas.');

    const clustersPerStrip: Record<string, number> = {};
    const draw = (): void => {
      context.clearRect(0, 0, canvas.width, canvas.height);
      // White so the shader can tint freely; alpha carries the shape.
      context.fillStyle = '#ffffff';
      context.strokeStyle = '#ffffff';
      context.lineJoin = 'round';
      context.lineCap = 'round';
      context.textAlign = 'center';
      context.textBaseline = 'middle';

      NOTE_WORDS.forEach((words, variant) => {
        drawNoteRow(context, ATLAS_ROWS.note.base + variant, rowHeight, noteCellWidth, words);
      });
      OUTER_MANTRAS.forEach((clusters, variant) => {
        clustersPerStrip[`outer${variant}`] =
          drawStrip(context, ATLAS_ROWS.outer.base + variant, rowHeight, width, clusters, 0.72);
      });
      SECONDARY_MANTRAS.forEach((clusters, variant) => {
        clustersPerStrip[`secondary${variant}`] =
          drawStrip(context, ATLAS_ROWS.secondary.base + variant, rowHeight, width, clusters, 0.70);
      });
      BIJA_SYLLABLES.forEach((clusters, variant) => {
        clustersPerStrip[`bija${variant}`] =
          drawStrip(context, ATLAS_ROWS.bija.base + variant, rowHeight, width, clusters, 0.68);
      });
      drawOrnamentRow(context, ATLAS_ROWS.ornament.base, rowHeight, width);
    };

    draw();

    const rowInk = measureRowInk(context, canvas.width, rowHeight, TOTAL_ROWS);
    const inkFraction = rowInk.reduce((sum, value) => sum + value, 0) / TOTAL_ROWS;
    // A platform with no Devanagari coverage draws either nothing or a wall of
    // identical boxes; both are worse than an abstract mark, so fall back. The
    // text rows are checked rather than the average, because the procedural
    // ornament row would render either way and could mask a total failure.
    const textRows = rowInk.slice(0, ATLAS_ROWS.ornament.base);
    const weakest = Math.min(...textRows);
    const strongest = Math.max(...textRows);
    const devanagariRendered = weakest > 0.01 && strongest < 0.55;
    if (!devanagariRendered) {
      drawFallbackAtlas(context, rowHeight, width, noteCellWidth, TOTAL_ROWS);
    }

    this.texture = new CanvasTexture(canvas);
    // The angle wraps, so U must repeat for the continuous strips; rows must not
    // bleed into each other, so V clamps.
    this.texture.wrapS = RepeatWrapping;
    this.texture.wrapT = ClampToEdgeWrapping;
    this.texture.minFilter = LinearFilter;
    this.texture.magFilter = LinearFilter;
    // Sampled with explicitly computed coordinates that jump at every ring
    // boundary; mipmaps would both blur the strokes and pick a garbage level
    // from that discontinuity.
    this.texture.generateMipmaps = false;
    // Written top-down, and the shader maps row order directly.
    this.texture.flipY = false;
    this.texture.needsUpdate = true;

    const bytes = canvas.width * canvas.height * 4;
    this.report = {
      rows: TOTAL_ROWS,
      rowHeight,
      width: canvas.width,
      height: canvas.height,
      noteWordsPerRing: NOTE_WORDS_PER_RING,
      noteCellAspect: Number(this.noteAspect.toFixed(3)),
      stripAspect: Number(this.stripAspect.toFixed(3)),
      clustersPerStrip,
      inkFraction: Number(inkFraction.toFixed(5)),
      rowInkFraction: rowInk.map((value) => Number(value.toFixed(4))),
      devanagariRendered,
      bytes,
      megabytes: Number((bytes / (1024 * 1024)).toFixed(2)),
    };
  }

  dispose(): void {
    this.texture.dispose();
  }
}

/** Six evenly spaced words, one per cell, for the note ring. */
function drawNoteRow(
  context: CanvasRenderingContext2D,
  row: number,
  rowHeight: number,
  cellWidth: number,
  words: readonly string[],
): void {
  const centreY = row * rowHeight + rowHeight * 0.52;
  const maxWidth = cellWidth * 0.88;
  const maxHeight = rowHeight * 0.76;
  for (let cell = 0; cell < NOTE_WORDS_PER_RING; cell += 1) {
    const word = words[cell % words.length] as string;
    const size = fitFontSize(context, word, maxWidth, maxHeight);
    context.font = `${size}px ${FONT_STACK}`;
    emboldenedText(context, word, cell * cellWidth + cellWidth * 0.5, centreY, size);
  }
}

/**
 * Running text that tiles seamlessly in U.
 *
 * Clusters are placed until the row would overflow, then the leftover width is
 * shared out as equal gaps. That is what makes the strip repeatable: the gap
 * after the last cluster is the same as every other gap, so wrapping around to
 * the first cluster looks like any other join. Returns how many clusters landed,
 * which is the density figure worth reporting.
 */
function drawStrip(
  context: CanvasRenderingContext2D,
  row: number,
  rowHeight: number,
  width: number,
  clusters: readonly string[],
  heightFraction: number,
): number {
  const size = Math.max(8, Math.floor(rowHeight * heightFraction));
  context.font = `${size}px ${FONT_STACK}`;
  const minimumGap = size * 0.22;

  const widths: number[] = [];
  let inkWidth = 0;
  let index = 0;
  while (inkWidth + widths.length * minimumGap < width) {
    const cluster = clusters[index % clusters.length] as string;
    const clusterWidth = context.measureText(cluster).width;
    widths.push(clusterWidth);
    inkWidth += clusterWidth;
    index += 1;
    // A pathological font could report zero width; stop rather than spin.
    if (clusterWidth <= 0.5 || widths.length > 400) break;
  }
  // The loop overshoots by one by construction; drop it so the gaps stay open.
  if (widths.length > 1) {
    inkWidth -= widths.pop() as number;
    index -= 1;
  }

  const count = widths.length;
  const gap = count > 0 ? (width - inkWidth) / count : 0;
  const centreY = row * rowHeight + rowHeight * 0.52;

  let x = gap * 0.5;
  for (let cell = 0; cell < count; cell += 1) {
    const cluster = clusters[cell % clusters.length] as string;
    const clusterWidth = widths[cell] as number;
    emboldenedText(context, cluster, x + clusterWidth * 0.5, centreY, size);
    x += clusterWidth + gap;
  }
  return count;
}

/**
 * Marks that sit between the text rings: diamonds, dots, bars and crosses.
 *
 * Drawn rather than typed, because the punctuation that would do this job is
 * not reliably present in a Devanagari font and a missing glyph here would be a
 * row of tofu boxes running round every seal.
 */
function drawOrnamentRow(
  context: CanvasRenderingContext2D,
  row: number,
  rowHeight: number,
  width: number,
): void {
  const top = row * rowHeight;
  const centreY = top + rowHeight * 0.5;
  const cells = 48;
  const cellWidth = width / cells;
  const unit = rowHeight * 0.24;
  context.lineWidth = Math.max(2, rowHeight * 0.05);

  for (let cell = 0; cell < cells; cell += 1) {
    const x = cell * cellWidth + cellWidth * 0.5;
    switch (cell % 6) {
      case 0: {
        context.beginPath();
        context.moveTo(x, centreY - unit);
        context.lineTo(x + unit * 0.8, centreY);
        context.lineTo(x, centreY + unit);
        context.lineTo(x - unit * 0.8, centreY);
        context.closePath();
        context.fill();
        break;
      }
      case 1:
      case 4: {
        context.beginPath();
        context.arc(x, centreY, unit * 0.34, 0, Math.PI * 2);
        context.fill();
        break;
      }
      case 2: {
        context.beginPath();
        context.moveTo(x - unit * 0.85, centreY + unit * 0.7);
        context.lineTo(x + unit * 0.85, centreY + unit * 0.7);
        context.lineTo(x, centreY - unit * 0.8);
        context.closePath();
        context.stroke();
        break;
      }
      case 3: {
        for (const offset of [-unit * 0.4, unit * 0.4]) {
          context.beginPath();
          context.moveTo(x + offset, centreY - unit);
          context.lineTo(x + offset, centreY + unit);
          context.stroke();
        }
        break;
      }
      default: {
        context.beginPath();
        context.moveTo(x - unit * 0.8, centreY);
        context.lineTo(x + unit * 0.8, centreY);
        context.moveTo(x, centreY - unit * 0.8);
        context.lineTo(x, centreY + unit * 0.8);
        context.stroke();
        break;
      }
    }
  }
}

/**
 * Stroke then fill, thickening every limb by a fixed fraction.
 *
 * On a ring twenty screen pixels tall, hairline Devanagari verticals fall below
 * one pixel and alias away entirely; this is what keeps a word a word rather
 * than a dotted line.
 */
function emboldenedText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  size: number,
): void {
  context.lineWidth = Math.max(1.5, size * 0.08);
  context.strokeText(text, x, y);
  context.fillText(text, x, y);
}

/** Largest size at which the word fits its cell, found by measurement. */
function fitFontSize(
  context: CanvasRenderingContext2D,
  glyph: string,
  maxWidth: number,
  maxHeight: number,
): number {
  let size = Math.floor(maxHeight);
  for (let attempt = 0; attempt < 24; attempt += 1) {
    context.font = `${size}px ${FONT_STACK}`;
    if (context.measureText(glyph).width <= maxWidth || size <= 8) break;
    size -= Math.max(1, Math.round(size * 0.06));
  }
  return size;
}

function measureRowInk(
  context: CanvasRenderingContext2D,
  width: number,
  rowHeight: number,
  rows: number,
): number[] {
  const fractions: number[] = [];
  for (let row = 0; row < rows; row += 1) {
    const data = context.getImageData(0, row * rowHeight, width, rowHeight).data;
    let ink = 0;
    for (let index = 3; index < data.length; index += 4) {
      if ((data[index] as number) > 24) ink += 1;
    }
    fractions.push(ink / (width * rowHeight));
  }
  return fractions;
}

/**
 * Abstract inscription marks, for platforms without a Devanagari font.
 *
 * Not an attempt at fake script — just rhythmic strokes under a connecting bar,
 * which is what reads as "inscribed ring" at the size these appear on screen.
 */
function drawFallbackAtlas(
  context: CanvasRenderingContext2D,
  rowHeight: number,
  width: number,
  noteCellWidth: number,
  rows: number,
): void {
  context.clearRect(0, 0, width, rowHeight * rows);
  context.strokeStyle = '#ffffff';
  context.fillStyle = '#ffffff';
  context.lineCap = 'round';

  for (let row = 0; row < rows; row += 1) {
    if (row === ATLAS_ROWS.ornament.base) {
      drawOrnamentRow(context, row, rowHeight, width);
      continue;
    }
    // Note rows keep the six wide cells; strips run tight so they still fill.
    const isNoteRow = row < ATLAS_ROWS.note.base + ATLAS_ROWS.note.variants;
    const cellWidth = isNoteRow ? noteCellWidth : rowHeight * 0.9;
    const cells = Math.max(1, Math.round(width / cellWidth));
    const step = width / cells;
    const top = row * rowHeight;
    const barY = top + rowHeight * 0.30;
    context.lineWidth = Math.max(2, rowHeight * 0.055);

    for (let cell = 0; cell < cells; cell += 1) {
      const x = cell * step;
      const inset = step * 0.12;
      context.beginPath();
      context.moveTo(x + inset, barY);
      context.lineTo(x + step - inset, barY);
      context.stroke();

      const strokes = 2 + ((row + cell) % 3);
      for (let stroke = 0; stroke < strokes; stroke += 1) {
        const sx = x + inset + ((step - inset * 2) * (stroke + 0.5)) / strokes;
        context.beginPath();
        context.moveTo(sx, barY);
        context.lineTo(sx, barY + rowHeight * (0.28 + 0.12 * ((row + stroke) % 3)));
        context.stroke();
      }
    }
  }
}
